import type { CrossroadsLog } from '@/api/hooks/useIntegrationLogs'
import {
  useAcceptOrderChange,
  useRejectOrderChange,
} from '@/api/hooks/useOrderChanges'
import {
  type OrderDetailPerspectiveView,
  useOrderDetails,
} from '@/api/hooks/useOrderDetails'
import type {
  DataPerspective,
  OrderPerspectiveView,
} from '@/api/hooks/useOrders'
import { LogDetailsDrawer } from '@/pages/Dashboard/panels/OrdersPanel/components/LogDetailsDrawer'
import { OrderChangeRequestDrawer } from '@/pages/Dashboard/panels/OrdersPanel/components/OrderChangeRequestDrawer'
import { OrderEmailsDrawer } from '@/pages/Dashboard/panels/OrdersPanel/components/OrderEmailsDrawer'
import { OrderHistoryDrawer } from '@/pages/Dashboard/panels/OrdersPanel/components/OrderHistoryDrawer'
import { useTenantStore } from '@/stores/tenantStore'
import {
  ArrowLeftOutlined,
  DollarOutlined,
  FileTextOutlined,
} from '@ant-design/icons'
import { Texto, Vertical } from '@gravitate-js/excalibrr'
import { Button, Empty, Spin, Tabs, message } from 'antd'
import { useCallback, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { InvoicesContent } from './InvoicesContent'
import { OrderDetailsContent } from './OrderDetailsContent'
import { OrderDetailsHeader } from './OrderDetailsHeader'

export function OrderDetails() {
  const navigate = useNavigate()
  const { id: orderId } = useParams<{ id: string }>()

  // Data perspective toggle (same as main dashboard)
  const [dataPerspective, setDataPerspective] =
    useState<DataPerspective>('internal_order')

  // Fetch order details
  const { data, isLoading, error } = useOrderDetails(orderId)

  // Order History drawer state
  const [historyOpen, setHistoryOpen] = useState(false)

  // Emails drawer state
  const [emailsOpen, setEmailsOpen] = useState(false)

  // Expanded view state
  const [isExpanded, setIsExpanded] = useState(false)

  // Active tab state
  const [activeTab, setActiveTab] = useState<'details' | 'invoices'>('details')

  // Change Request drawer state - only store changeId, construct order from perspectiveView when rendering
  const [changeRequestChangeId, setChangeRequestChangeId] = useState<
    string | null
  >(null)

  // Log details drawer state -- the log comes off the row rather than being
  // looked up, same as the orders grid.
  const [selectedLog, setSelectedLog] = useState<{
    log: CrossroadsLog
    logGroupId: string | null
  } | null>(null)
  const selectedTenantName = useTenantStore().selectedTenant?.name

  // Accept/Reject mutations
  const acceptMutation = useAcceptOrderChange()
  const rejectMutation = useRejectOrderChange()

  const handleAcceptChange = useCallback(() => {
    if (!changeRequestChangeId) return

    acceptMutation.mutate(
      { body: { id: changeRequestChangeId } },
      {
        onSuccess: () => {
          message.success('Order change accepted')
          setChangeRequestChangeId(null)
        },
        onError: (error) => {
          const errorMsg =
            error instanceof Error ? error.message : 'Failed to accept change'
          message.error(errorMsg)
        },
      },
    )
  }, [changeRequestChangeId, acceptMutation])

  const handleRejectChange = useCallback(() => {
    if (!changeRequestChangeId) return

    rejectMutation.mutate(
      { body: { id: changeRequestChangeId } },
      {
        onSuccess: () => {
          message.success('Order change rejected')
          setChangeRequestChangeId(null)
        },
        onError: (error) => {
          const errorMsg =
            error instanceof Error ? error.message : 'Failed to reject change'
          message.error(errorMsg)
        },
      },
    )
  }, [changeRequestChangeId, rejectMutation])

  // Handle change request click - defined before early returns to satisfy Rules of Hooks
  const handleChangeRequestClick = useCallback((changeId: string) => {
    setChangeRequestChangeId(changeId)
  }, [])

  const handleLogClick = useCallback(
    (log: CrossroadsLog, logGroupId: string | null) => {
      setSelectedLog({ log, logGroupId })
    },
    [],
  )

  // Get the appropriate perspective view
  const getPerspectiveView = (
    row: typeof data,
  ): OrderDetailPerspectiveView | null => {
    if (!row?.order) return null
    switch (dataPerspective) {
      case 'gravitate_order':
        return row.order.gravitate_order ?? row.order.internal_order
      case 'partner_order':
        return row.order.partner_order ?? row.order.internal_order
      case 'internal_order':
      default:
        return row.order.internal_order
    }
  }

  const perspectiveView = getPerspectiveView(data)

  const handleBack = () => {
    navigate('/operations/orders')
  }

  const handleHistoryClick = () => {
    setHistoryOpen(true)
  }

  const handleEmailsClick = () => {
    setEmailsOpen(true)
  }

  if (isLoading) {
    return (
      <Vertical
        className='h-full w-full'
        style={{ alignItems: 'center', justifyContent: 'center' }}
      >
        <Spin size='large' />
        <Texto
          appearance='hint'
          style={{ marginTop: 12, color: 'var(--muted-foreground)' }}
        >
          Loading order details...
        </Texto>
      </Vertical>
    )
  }

  if (error || !perspectiveView) {
    return (
      <Vertical
        className='h-full w-full'
        style={{ alignItems: 'center', justifyContent: 'center' }}
      >
        <Empty description='Order not found' />
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={handleBack}
          style={{ marginTop: 16 }}
        >
          Back to Orders
        </Button>
      </Vertical>
    )
  }

  // Convert perspective view to OrderPerspectiveView for drawers
  const orderForDrawers: OrderPerspectiveView = {
    ...perspectiveView,
  }

  return (
    <Vertical className='w-full' style={{ height: '100%', overflow: 'auto' }}>
      {/* Content area */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: '20px 32px',
          maxWidth: isExpanded ? 3000 : 1900,
          margin: '0 auto',
          width: '100%',
          transition: 'max-width 0.3s ease-in-out',
        }}
      >
        {/* Header with order info */}
        <div style={{ flexShrink: 0 }}>
          <OrderDetailsHeader
            order={perspectiveView}
            dataPerspective={dataPerspective}
            onDataPerspectiveChange={setDataPerspective}
            onHistoryClick={handleHistoryClick}
            onEmailsClick={handleEmailsClick}
            isExpanded={isExpanded}
            onExpandToggle={() => setIsExpanded(!isExpanded)}
            onBackClick={handleBack}
            onChangeRequestClick={handleChangeRequestClick}
            log={data?.order?.log}
            logGroupId={data?.order?.log_group_id}
            onLogClick={handleLogClick}
            flags={data?.order?.flags}
          />
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as 'details' | 'invoices')}
          items={[
            {
              key: 'details',
              label: 'Order Details',
              icon: <FileTextOutlined style={{ fontSize: 14 }} />,
              children: (
                <OrderDetailsContent perspectiveView={perspectiveView} />
              ),
            },
            {
              key: 'invoices',
              label: 'Invoices',
              icon: <DollarOutlined style={{ fontSize: 14 }} />,
              children: (
                <InvoicesContent
                  orderId={orderId}
                  dataPerspective={dataPerspective}
                />
              ),
            },
          ]}
        />

        {/* Order History Drawer */}
        <OrderHistoryDrawer
          order={orderForDrawers}
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
        />

        {/* Order Emails Drawer */}
        <OrderEmailsDrawer
          orderId={orderId}
          open={emailsOpen}
          onClose={() => setEmailsOpen(false)}
        />

        {/* Log Details Drawer */}
        <LogDetailsDrawer
          logGroupId={selectedLog?.logGroupId ?? null}
          log={selectedLog?.log ?? null}
          tenantName={selectedTenantName}
          onClose={() => setSelectedLog(null)}
        />

        {/* Order Change Request Drawer */}
        <OrderChangeRequestDrawer
          order={changeRequestChangeId ? orderForDrawers : null}
          changeId={changeRequestChangeId}
          open={!!changeRequestChangeId}
          onClose={() => setChangeRequestChangeId(null)}
          onAccept={handleAcceptChange}
          onReject={handleRejectChange}
          isAccepting={acceptMutation.isPending}
          isRejecting={rejectMutation.isPending}
        />
      </div>
    </Vertical>
  )
}
